import { Injectable } from '@angular/core';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { ToastrService } from 'ngx-toastr';
import {
  MigrationsActionTypes,
  GetMigrationsSuccess,
  GetMigrationsFail,
  GetAccountMigrationsSuccess,
  GetAccountMigrationsFail,
  GetMigrationReportSuccess,
  GetMigrationReportFail,
  GetAccountMigrationProgress,
  GetAccountMigrationProgressFail,
  GetAccountMigrationProgressSuccess,
  GetAccountPASMigrationsSuccess,
  GetAccountPASMigrationsFail,
  GetAccountPASMigrations,
} from './migrations.actions';
import * as fromMigrations from './migrations.reducer';
import { map, catchError, switchMap, mergeMap } from 'rxjs/operators';
import { MigrationsService } from '@migrations/services/migrations.service';
import { IMigration, IAccountMigration, IMigrationStatus } from '@shared';
import { Store } from '@ngrx/store';

@Injectable()
export class MigrationsEffects {
  @Effect()
  getMigrations$ = this.actions$.pipe(
    ofType(MigrationsActionTypes.GetMigrations),
    switchMap(() => {
      return this.migrationsService.fetchMigrations().pipe(
        map((response: IMigration[]) => {
          return new GetMigrationsSuccess(response);
        }),
        catchError((error) => {
          error.status === 503
            ? this.toastr.error('Please retry again later', 'Request failed')
            : this.toastr.error('Could not fetch migrations', 'Request failed');
          return [new GetMigrationsFail(error)];
        }),
      );
    }),
  );

  @Effect()
  getAccountPASMigrations$ = this.actions$.pipe(
    ofType(MigrationsActionTypes.GetAccountPASMigrations),
    switchMap(({ payload: { accountId, proAccountId } }) => {
      return this.migrationsService.fetchAccountPASMigration(accountId).pipe(
        map((migrations: IMigration[]) => {
          const migration = migrations.find(
            (m) => m.proAccountId === proAccountId,
          );
          return new GetAccountPASMigrationsSuccess({ migration });
        }),
        catchError(() => {
          this.toastr.error('Could not fetch migrations', 'Request failed');
          return [new GetAccountPASMigrationsFail()];
        }),
      );
    }),
  );

  @Effect()
  getAccountMigrations$ = this.actions$.pipe(
    ofType(MigrationsActionTypes.GetAccountMigrations),
    switchMap(({ payload: { accountId, proAccountId, startDate } }) => {
      return this.migrationsService
        .getAccountMigrations(accountId, startDate)
        .pipe(
          map((migrations: IAccountMigration[]) => {
            const requestedMigrations = migrations.filter(
              (m) => m.proAccountId === proAccountId,
            );
            if (requestedMigrations?.length) {
              const lastRunEHM = requestedMigrations.find(
                (m) => m.type === 'EHM',
              );
              if (lastRunEHM) {
                this.store.dispatch(
                  new GetAccountMigrationProgress({
                    oneAccountId: lastRunEHM.accountId,
                    proAccountId: proAccountId,
                    sectionId: lastRunEHM.sectionId,
                  }),
                );
              }
              this.store.dispatch(
                new GetAccountPASMigrations({
                  accountId: accountId,
                  proAccountId: proAccountId,
                }),
              );
            }
            return new GetAccountMigrationsSuccess(requestedMigrations);
          }),
          catchError((error) => {
            error.status === 503
              ? this.toastr.error('Please retry again later', 'Request failed')
              : this.toastr.error(
                  'Could not fetch migrations',
                  'Request failed',
                );
            return [new GetAccountMigrationsFail(error)];
          }),
        );
    }),
  );

  @Effect()
  getMigrationReport$ = this.actions$.pipe(
    ofType(MigrationsActionTypes.GetMigrationReport),
    switchMap(({ payload }) => {
      return this.migrationsService.getMigrationReport(payload).pipe(
        map(({ url }) => {
          window.open(url);
          return new GetMigrationReportSuccess();
        }),
        catchError((error) => {
          error.status === 400
            ? this.toastr.error('No logs available', 'Request failed')
            : this.toastr.error('Could not fetch logs', 'Request failed');
          return [new GetMigrationReportFail(error)];
        }),
      );
    }),
  );

  @Effect()
  getAccountMigrationStatus$ = this.actions$.pipe(
    ofType(MigrationsActionTypes.GetAccountMigrationProgress),
    mergeMap(({ payload }) => {
      return this.migrationsService.getMigrationProgress(payload).pipe(
        map(
          (response: IMigrationStatus) =>
            new GetAccountMigrationProgressSuccess({ progress: response }),
        ),
        catchError(() => [new GetAccountMigrationProgressFail()]),
      );
    }),
  );

  constructor(
    private toastr: ToastrService,
    private actions$: Actions,
    private store: Store<fromMigrations.State>,
    private migrationsService: MigrationsService,
  ) {}
}
